import { CircleAlert, Upload } from "lucide-react";
import { useCallback, useId, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { useNavigate } from "react-router";

import { ApiError, uploadDocument } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { StepHeader } from "@/flow/StepHeader";
import { buttonClass } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

type Phase =
  { kind: "idle" } | { kind: "uploading"; name: string } | { kind: "error"; msg: string };

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

/** Step one: pick or drop one PDF set. On success, go on to choosing its sheets. */
export function UploadScreen() {
  const navigate = useNavigate();
  const canUpload = useCan("documents:upload");
  const inputId = useId();
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const submit = useCallback(
    async (file: File | undefined) => {
      if (!file) {
        return;
      }
      if (!isPdf(file)) {
        setPhase({ kind: "error", msg: `${file.name} is not a PDF. Upload one PDF drawing set.` });
        return;
      }
      setPhase({ kind: "uploading", name: file.name });
      try {
        const doc = await uploadDocument(file);
        await navigate(`/documents/${doc.id}/sheets`);
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? `Upload rejected (${err.status}): ${err.detail}`
            : "Upload failed.";
        setPhase({ kind: "error", msg });
      }
    },
    [navigate],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setActive(false);
      void submit(event.dataTransfer.files[0]);
    },
    [submit],
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      void submit(event.target.files?.[0]);
      event.target.value = "";
    },
    [submit],
  );

  if (!canUpload) {
    return (
      <main className="min-h-full">
        <StepHeader step="upload" />
        <div className="mx-auto max-w-3xl px-4 py-10 lg:py-14">
          <Notice tone="warn">
            Your role can open and read sets but not upload them. An owner, admin or estimator can.
          </Notice>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full">
      <StepHeader step="upload" />
      <div className="mx-auto max-w-3xl px-4 py-10 lg:py-14">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">New set</h1>
          <p className="mt-1.5 max-w-xl text-[15px] text-muted">
            Upload the drawing set as one PDF. You choose which sheets to work from, and every panel
            on them is marked up and counted by size.
          </p>
        </header>
        <div
          className={cn(
            "flex flex-col items-center justify-center rounded-card border-2 border-dashed px-6 py-14 text-center transition-colors",
            active
              ? "border-accent bg-accent-soft"
              : "border-line-strong bg-surface hover:border-accent/60",
          )}
          onDragOver={(event) => {
            event.preventDefault();
            setActive(true);
          }}
          onDragLeave={() => setActive(false)}
          onDrop={onDrop}
        >
          {phase.kind === "uploading" ? (
            <p className="flex items-center gap-3 text-[15px] text-ink" role="status">
              <Spinner className="text-accent" /> Uploading {phase.name}…
            </p>
          ) : (
            <>
              <span className="mb-4 grid size-12 place-items-center rounded-full bg-accent-soft text-accent">
                <Upload aria-hidden="true" className="size-6" />
              </span>
              <p className="text-[15px] font-medium text-ink">Drop a PDF drawing set here</p>
              <p className="mt-1 text-sm text-muted">The whole set in one file, up to 200 MB</p>
              <label
                htmlFor={inputId}
                className={cn(buttonClass("primary", "md"), "mt-5 cursor-pointer")}
              >
                Choose a PDF
              </label>
              <input
                id={inputId}
                className="sr-only"
                type="file"
                accept="application/pdf,.pdf"
                onChange={onChange}
              />
            </>
          )}
        </div>
        {phase.kind === "error" ? (
          <p
            className="mt-4 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2.5 text-sm text-danger"
            role="alert"
          >
            <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            {phase.msg}
          </p>
        ) : null}
      </div>
    </main>
  );
}
